import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Table, Button, Input, Space, Tag, Card, Row, Col, Select, Statistic } from 'antd'
import {
  EyeOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UploadOutlined,
  DownloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { hospitalsApi } from '@/api/hospitals'
import type { Hospital } from '@/types/hospital'
import { HospitalImportWizard } from '@/components/hospitals/HospitalImportWizard'
import { HospitalExportDialog } from '@/components/hospitals/HospitalExportDialog'

const { Search } = Input

export const HospitalList = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<string | undefined>(undefined)
  const [participantFilter, setParticipantFilter] = useState<boolean | undefined>(undefined)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)

  // Fetch hospitals with React Query
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['hospitals', page, pageSize, search, stateFilter, participantFilter],
    queryFn: () =>
      hospitalsApi.getHospitals({
        page,
        pageSize,
        search: search || undefined,
        state: stateFilter,
        participant: participantFilter,
      }),
  })

  // Fetch states for filter dropdown
  const { data: statesData } = useQuery({
    queryKey: ['hospital-states'],
    queryFn: () => hospitalsApi.getStates(),
  })

  // Calculate statistics
  const stats = {
    total: data?.data.total || 0,
    participants: data?.data.hospitals.filter((h) => h.participant).length || 0,
  }

  const columns: ColumnsType<Hospital> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => <a onClick={() => navigate(`/hospitals/${record.id}`)}>{name}</a>,
    },
    {
      title: 'CMS ID',
      dataIndex: 'cmsid',
      key: 'cmsid',
      width: 100,
      render: (cmsid) => <Tag color="blue">{cmsid}</Tag>,
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <Space size={0} direction="vertical">
          <div>
            {record.city}, {record.state}
          </div>
          {record.county && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              <EnvironmentOutlined /> {record.county} County
            </div>
          )}
        </Space>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => (
        <Space size={0} direction="vertical">
          {record.type && <div>{record.type}</div>}
          {record.facility_type && (
            <div style={{ fontSize: '12px', color: '#888' }}>{record.facility_type}</div>
          )}
        </Space>
      ),
    },
    {
      title: 'Features',
      key: 'features',
      render: (_, record) => {
        const features: Array<{ label: string; color: string }> = [
          record.participant && { label: 'Participant', color: 'blue' },
          record.emergency && { label: 'Emergency', color: 'red' },
          record.is_transfer_center && { label: 'Transfer Center', color: 'purple' },
          record.critical_access && { label: 'Critical Access', color: 'orange' },
        ].filter(Boolean) as Array<{ label: string; color: string }>

        return (
          <Space wrap size={[4, 8]}>
            {features.map((feature, i) => (
              <Tag key={i} color={feature.color} style={{ fontSize: '11px', padding: '0 4px' }}>
                {feature.label}
              </Tag>
            ))}
            {features.length === 0 && <span style={{ color: '#888' }}>-</span>}
          </Space>
        )
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) =>
        phone ? (
          <a href={`tel:${phone}`}>
            <PhoneOutlined /> {phone}
          </a>
        ) : (
          <span style={{ color: '#888' }}>-</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/hospitals/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ]

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <h1 style={{ margin: 0 }}>Hospital Management</h1>
        </Col>
        <Col>
          <Space size="large">
            <Statistic title="Total Hospitals" value={stats.total} />
            <Statistic
              title="Participants"
              value={stats.participants}
              valueStyle={{ color: '#1677ff' }}
            />
          </Space>
        </Col>
      </Row>

      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<HistoryOutlined />} onClick={() => navigate('/hospitals/imports')}>
              Import History
            </Button>
            <Button icon={<DownloadOutlined />} onClick={() => setExportModalOpen(true)}>
              Export
            </Button>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setImportModalOpen(true)}
            >
              Import
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col flex="auto">
              <Search
                placeholder="Search by hospital name, city, or CMS ID"
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                onSearch={setSearch}
              />
            </Col>
            <Col>
              <Select
                placeholder="Filter by state"
                allowClear
                style={{ width: 150 }}
                size="large"
                onChange={setStateFilter}
                value={stateFilter}
                options={
                  statesData?.data.map((state) => ({
                    label: state,
                    value: state,
                  })) || []
                }
              />
            </Col>
            <Col>
              <Select
                placeholder="Participant"
                allowClear
                style={{ width: 150 }}
                size="large"
                onChange={setParticipantFilter}
                value={participantFilter}
              >
                <Select.Option value={true}>Participant</Select.Option>
                <Select.Option value={false}>Non-Participant</Select.Option>
              </Select>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={data?.data.hospitals || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: data?.data.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} hospitals`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage)
                setPageSize(newPageSize)
              },
            }}
            scroll={{ x: 1200 }}
          />
        </Space>
      </Card>

      <HospitalImportWizard
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={() => {
          refetch()
          setImportModalOpen(false)
        }}
      />

      <HospitalExportDialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        availableStates={statesData?.data || []}
      />
    </div>
  )
}
